import { useEffect, useRef, useState } from "react";
import { Activity, Rocket, Timer, Radio } from "lucide-react";

export type Status = "design" | "dev" | "review" | "ready";

export const STATUS_META: Record<Status, { label: string; dot: string; chip: string }> = {
  design: {
    label: "In design",
    dot: "bg-violet-400 shadow-[0_0_10px_theme(colors.violet.400)]",
    chip: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  },
  dev: {
    label: "In sviluppo",
    dot: "bg-sky-400 shadow-[0_0_10px_theme(colors.sky.400)]",
    chip: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  },
  review: {
    label: "In revisione",
    dot: "bg-amber-400 shadow-[0_0_10px_theme(colors.amber.400)]",
    chip: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  },
  ready: {
    label: "Pronto alla consegna",
    dot: "bg-emerald-400 shadow-[0_0_10px_theme(colors.emerald.400)]",
    chip: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  },
};

export type Project = {
  id: string;
  name: string;
  type: string;
  status: Status;
  progress: number;
  etaHours: number;
  updatedMinAgo: number;
};

export const PROJECTS: Project[] = [
  { id: "p1", name: "Studio Dentistico Milano", type: "Sito aziendale · 5 pagine", status: "review", progress: 86, etaHours: 9, updatedMinAgo: 4 },
  { id: "p2", name: "E-commerce Fashion ZRH", type: "Shop · 24 prodotti", status: "dev", progress: 62, etaHours: 22, updatedMinAgo: 11 },
  { id: "p3", name: "Ristorante Lago di Como", type: "Landing + menu digitale", status: "ready", progress: 98, etaHours: 2, updatedMinAgo: 1 },
  { id: "p4", name: "Studio Avvocati Lugano", type: "Sito aziendale · 4 pagine", status: "design", progress: 24, etaHours: 38, updatedMinAgo: 17 },
  { id: "p5", name: "Clinica Estetica Zurigo", type: "Landing booking", status: "dev", progress: 54, etaHours: 26, updatedMinAgo: 6 },
  { id: "p6", name: "NCC Premium Ticino", type: "Landing + preventivo", status: "review", progress: 78, etaHours: 14, updatedMinAgo: 9 },
  { id: "p7", name: "Palestra CrossFit Bellinzona", type: "Landing + iscrizioni", status: "dev", progress: 48, etaHours: 30, updatedMinAgo: 3 },
  { id: "p8", name: "Boutique Hotel Ascona", type: "Sito · 8 pagine + booking", status: "review", progress: 82, etaHours: 12, updatedMinAgo: 7 },
  { id: "p9", name: "Studio Architetti Basilea", type: "Portfolio · 6 progetti", status: "design", progress: 31, etaHours: 44, updatedMinAgo: 22 },
  { id: "p10", name: "Panetteria Artigianale Como", type: "Landing + ordini online", status: "ready", progress: 96, etaHours: 3, updatedMinAgo: 2 },
  { id: "p11", name: "Agenzia Immobiliare Chiasso", type: "Sito · listing immobili", status: "dev", progress: 67, etaHours: 20, updatedMinAgo: 14 },
  { id: "p12", name: "Concessionario Auto Ginevra", type: "Showroom digitale · 32 auto", status: "review", progress: 74, etaHours: 16, updatedMinAgo: 5 },
  { id: "p13", name: "Yoga Studio Locarno", type: "Landing + calendario corsi", status: "design", progress: 18, etaHours: 46, updatedMinAgo: 25 },
  { id: "p14", name: "Pasticceria Milano Centro", type: "E-commerce · 40 prodotti", status: "dev", progress: 58, etaHours: 24, updatedMinAgo: 8 },
  { id: "p15", name: "Studio Fotografico Zurigo", type: "Portfolio + shop stampe", status: "ready", progress: 94, etaHours: 4, updatedMinAgo: 1 },
  { id: "p16", name: "Consulenza Finanziaria Zug", type: "Sito aziendale · 6 pagine", status: "review", progress: 80, etaHours: 11, updatedMinAgo: 6 },
  { id: "p17", name: "Centro Benessere Lugano", type: "Landing + prenotazioni", status: "dev", progress: 45, etaHours: 32, updatedMinAgo: 12 },
  { id: "p18", name: "Startup SaaS Losanna", type: "Landing prodotto + waitlist", status: "design", progress: 27, etaHours: 40, updatedMinAgo: 19 },
];

function pickRandom<T>(arr: T[], n: number): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}


function useCountUp<T extends HTMLElement = HTMLSpanElement>(target: number, durationMs = 1400) {
  const [value, setValue] = useState(0);
  const ref = useRef<T>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (t: number) => {
              const p = Math.min(1, (t - start) / durationMs);
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(target * eased);
              if (p < 1) requestAnimationFrame(tick);
              else setValue(target);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, durationMs]);
  return { value, ref };
}

function StatCounter({ icon: Icon, label, target, suffix = "" }: { icon: typeof Activity; label: string; target: number; suffix?: string }) {
  const { value, ref } = useCountUp(target);
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-border bg-background/60 p-5 backdrop-blur">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="font-display text-3xl font-bold tracking-tight">
          <span ref={ref}>{Math.round(value)}</span>
          {suffix && <span className="ml-1 text-xl text-muted-foreground">{suffix}</span>}
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function ProgressBar({ value }: { value: number }) {
  const { value: animated, ref } = useCountUp<HTMLDivElement>(value, 1600);
  return (
    <div ref={ref} className="relative h-1.5 w-full overflow-hidden rounded-full bg-border/60">
      <div
        className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary to-primary/60 transition-[width] duration-300"
        style={{ width: `${animated}%` }}
      />
      <div
        className="absolute inset-y-0 w-8 -translate-x-full animate-[shimmer_2.4s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent"
        style={{ left: `${animated}%` }}
      />
    </div>
  );
}

export function ProjectCard({ p }: { p: Project }) {
  const meta = STATUS_META[p.status];
  const { value: pct, ref } = useCountUp(p.progress, 1600);
  const eta = p.etaHours >= 24 ? `~${Math.round(p.etaHours / 24)} giorni` : `~${p.etaHours}h`;
  return (
    <div className="group relative flex flex-col rounded-2xl border border-border bg-background/70 p-5 backdrop-blur transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-[0_10px_40px_-15px_var(--primary)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-display text-base font-semibold">{p.name}</h3>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{p.type}</p>
        </div>
        <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${meta.chip}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${meta.dot} animate-pulse`} />
          {meta.label}
        </span>
      </div>

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Avanzamento</span>
          <span ref={ref} className="font-display font-bold text-foreground">{Math.round(pct)}%</span>
        </div>
        <ProgressBar value={p.progress} />
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-border/60 pt-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <Timer className="h-3.5 w-3.5 text-primary" />
          ETA {eta}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_theme(colors.emerald.400)] animate-pulse" />
          aggiornato {p.updatedMinAgo} min fa
        </span>
      </div>
    </div>
  );
}

export function LiveActivitySection() {
  const [visible, setVisible] = useState<Project[]>(() => PROJECTS.slice(0, 6));
  useEffect(() => {
    setVisible(pickRandom(PROJECTS, 6));
    const id = setInterval(() => setVisible(pickRandom(PROJECTS, 6)), 12000);
    return () => clearInterval(id);
  }, []);
  return (

    <section id="live" className="relative border-y border-border bg-card/40">
      <div className="pointer-events-none absolute inset-0 opacity-50" style={{ background: "var(--gradient-hero)" }} />
      <div className="relative mx-auto w-full max-w-6xl px-6 py-16 sm:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-rose-300 backdrop-blur">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
            </span>
            Live Activity
          </div>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Progetti <span className="text-gradient">in lavorazione</span> in questo momento
          </h2>
          <p className="mt-4 text-muted-foreground">
            Ogni sito viene seguito passo dopo passo. Qui puoi vedere lo stato reale dei progetti
            attivi e il nostro flusso di lavoro.
          </p>
        </div>

        {/* Dashboard header strip */}
        <div className="mt-10 overflow-hidden rounded-t-2xl border border-b-0 border-border bg-background/80 px-5 py-3 backdrop-blur">
          <div className="flex items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 text-muted-foreground">
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
              </div>
              <span className="ml-2 font-mono">flashweb / control-room</span>
            </div>
            <div className="hidden items-center gap-2 text-muted-foreground sm:flex">
              <Radio className="h-3.5 w-3.5 text-emerald-400 animate-pulse" />
              <span className="font-mono">system online</span>
            </div>
          </div>
        </div>

        {/* Projects grid */}
        <div className="rounded-b-2xl border border-border bg-background/30 p-4 backdrop-blur sm:p-5">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((p) => (
              <ProjectCard key={p.id} p={p} />
            ))}
          </div>
        </div>

        {/* Live stats */}
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <StatCounter icon={Activity} label="Siti in lavorazione" target={18} />
          <StatCounter icon={Rocket} label="Progetti consegnati questo mese" target={6 + new Date().getDate() - 1} />
          <StatCounter icon={Timer} label="Tempo medio di consegna" target={48} suffix="h" />
        </div>

        {/* Trust box */}
        <div className="mt-8 rounded-2xl border border-border bg-background/60 p-6 backdrop-blur sm:p-8">
          <h3 className="font-display text-xl font-bold sm:text-2xl">
            Trasparenza totale. Nessuna promessa, solo lavoro reale.
          </h3>
          <p className="mt-3 text-sm text-muted-foreground sm:text-base">
            Ogni progetto è seguito passo dopo passo. Non vendiamo "siti standard", ma consegniamo
            prodotti costruiti e rifiniti uno per uno in tempi rapidi e controllati.
          </p>
        </div>
      </div>
    </section>
  );
}
